import {
  Avatar,
  Checkbox,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { fetchUsers, fetchCompetition, assignUsers } from "apis/compete";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";
import React, { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import InfoIcon from "@mui/icons-material/Info";

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy]?.toLowerCase() < a[orderBy]?.toLowerCase()) {
    return -1;
  }
  if (b[orderBy]?.toLowerCase() > a[orderBy]?.toLowerCase()) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, isMobileDisplay } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = isMobileDisplay
    ? [
        {
          id: "name",
          numeric: false,
          disablePadding: true,
          label: "Name",
        },
      ]
    : [
        {
          id: "name",
          numeric: false,
          disablePadding: true,
          label: "Name",
        },
        {
          id: "location",
          numeric: false,
          disablePadding: false,
          label: "Location",
        },
        {
          id: "team",
          numeric: false,
          disablePadding: false,
          label: "Team",
        },
        {
          id: "role",
          numeric: false,
          disablePadding: false,
          label: "Role",
        },
      ];

  return (
    <TableHead sx={{ display: "table-header-group" }}>
      <TableRow>
        <TableCell />
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{ fontSize: "0.9rem", fontWeight: "400", color: "#888" }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <MDBox component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </MDBox>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function SelectParticipants({
  open,
  setOpen,
  users: selectedUsers,
  form,
  selectParticipants,
  competition,
  setCompetition,
}) {
  const [users, setUsers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userLoading, setUserLoading] = useState(false);
  const [competitionLoading, setCompetitionLoading] = useState(false);
  const [initialCompetitionUsers, setInitialCompetitionUsers] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));

  const handleClose = () => {
    if (!submitLoading) {
      setUsers([]);
      setSearchValue("");
      setOrder("asc");
      setOrderBy("name");
      setSelected([]);
      setRowsPerPage(10);
      setPage(0);
      setOpen(false);
      setInitialCompetitionUsers([]);
      if (!form) {
        setCompetition();
      }
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (selectAll) => {
    if (selectAll) {
      const newSelected = [...users];
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, row) => {
    const selectedIndex = selected.findIndex((user) => user.id === row.id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (row) => selected.find((user) => user.id === row.id);

  const [controller] = useMaterialUIController();

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "800px",
    maxHeight: window.innerHeight < 800 ? 600 : 700,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    overflow: "auto",
    boxShadow: 24,
    p: 2,
    outline: "none",
    borderRadius: "0.5rem",
  };

  const handleSearchFilter = (user) => {
    if (user?.disabled && !isSelected(user)) return false;
    if (!searchValue) {
      return true;
    }
    return (
      user?.name?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      user?.location?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      user?.team?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      user?.role?.toLowerCase()?.includes(searchValue?.toLowerCase())
    );
  };

  const getUsers = async () => {
    try {
      setUserLoading(true);
      const response = await fetchUsers();
      if (response?.data?.success) {
        setUsers(response?.data?.data);
        if (form) {
          setSelected(selectedUsers);
        }
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      } else if (response?.response?.status === 403) {
        handleClose();
      }
      setUserLoading(false);
    } catch (error) {
      setUserLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const getCompetition = async () => {
    try {
      setCompetitionLoading(true);
      const response = await fetchCompetition(competition?.id);
      if (response?.data?.success) {
        const usersArray = response?.data?.data?.CompetitionMembers?.map((competitionUser) => ({
          id: competitionUser?.userId,
          name: competitionUser?.User?.name,
        }));
        setInitialCompetitionUsers(usersArray);
        setSelected(usersArray);
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      } else if (response?.response?.status === 403) {
        handleClose();
      }
      setCompetitionLoading(false);
    } catch (error) {
      setCompetitionLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (open) {
      getUsers();
    }
  }, [open]);

  useEffect(() => {
    if (open && !form) {
      getCompetition();
    }
  }, [competition?.id]);

  const handleUserAssign = async () => {
    try {
      setSubmitLoading(true);

      const removeUserAssignIds = initialCompetitionUsers
        .filter(
          (initialUser) => !selected.some((selectedUser) => initialUser.id === selectedUser.id)
        )
        .map((removedUser) => removedUser.id);

      const assignUserList = selected.filter(
        (selectedUser) =>
          !initialCompetitionUsers.some((newSelectedUser) => newSelectedUser.id === selectedUser.id)
      );

      const response = await assignUsers({
        competitionId: competition.id,
        removeUserAssignIds,
        assignUserList,
      });
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response.data.message || "Record Updated Successfully",
          }),
          {
            variant: "custom",
          }
        );
        handleClose();
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      } else if (response?.response?.status === 403) {
        handleClose();
      }

      setSubmitLoading(false);
    } catch (error) {
      setSubmitLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleParticipantsSelect = () => {
    if (form) {
      selectParticipants(selected);
      handleClose();
    } else {
      handleUserAssign();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDBox sx={{ position: "relative" }}>
          <Loader loading={userLoading || competitionLoading} />
          <Stack direction="row" alignItems="center" mb={1} spacing={1} ml={1} mt={0.5}>
            <MDTypography variant="h4">Pick Your Competitors</MDTypography>
            {!isMobileDisplay && (
              <Tooltip title="Users with Input KPI Permission enabled can be selected as competitors.">
                <InfoIcon />
              </Tooltip>
            )}
          </Stack>
          <MDTypography variant="subtitle2" sx={{ color: "#7b809a", fontSize: "1rem", ml: 1 }}>
            Select who will be added to the competition
          </MDTypography>

          <MDBox sx={{ mt: 0.5, ml: 1, display: "flex", justifyContent: "space-between" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <MDTypography
                sx={{
                  fontSize: isMobileDisplay ? "0.9rem" : "1rem",
                  color: "#1a73e8",
                  cursor: "pointer",
                }}
                onClick={() => handleSelectAllClick(true)}
              >
                Select All
              </MDTypography>
              <MDTypography sx={{ fontSize: "1.25rem", fontWeight: "600" }}>|</MDTypography>
              <MDTypography
                sx={{
                  fontSize: isMobileDisplay ? "0.9rem" : "1rem",
                  color: "#1a73e8",
                  cursor: "pointer",
                }}
                onClick={() => handleSelectAllClick(false)}
              >
                Deselect All
              </MDTypography>
            </Stack>
            <MDInput
              sx={{ width: "50%", maxWidth: "150px" }}
              label="Search"
              placeholder="Search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </MDBox>

          <MDBox sx={{ mt: 1 }}>
            <Paper sx={{ boxShadow: "none", "& .MuiTableContainer-root": { boxShadow: "none" } }}>
              <TableContainer className="compete-users-table">
                <Table aria-labelledby="tableTitle">
                  <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={users.length}
                    isMobileDisplay={isMobileDisplay}
                  />
                  <TableBody>
                    {stableSort(users.filter(handleSearchFilter), getComparator(order, orderBy))
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => {
                        const isItemSelected = !!isSelected(row);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            onClick={(event) => !row?.disabled && handleClick(event, row)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.name}
                            selected={isItemSelected}
                            sx={[
                              row?.disabled && {
                                cursor: "not-allowed",
                              },
                            ]}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{
                                  "aria-labelledby": labelId,
                                }}
                                disabled={row?.disabled}
                              />
                            </TableCell>
                            <TableCell component="th" id={labelId} scope="row" padding="none">
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar
                                  variant="circular"
                                  src={row?.profilePicture}
                                  sx={{
                                    width: isMobileDisplay ? "30px" : "40px",
                                    height: isMobileDisplay ? "30px" : "40px",
                                    "& .MuiAvatar-img ": { height: "100% !important" },
                                  }}
                                />
                                <MDTypography
                                  sx={{
                                    fontSize: isMobileDisplay ? "0.8rem" : "0.9rem",
                                    fontWeight: "300",
                                  }}
                                >
                                  {row.name}
                                </MDTypography>
                              </Stack>
                            </TableCell>
                            {isMobileDisplay ? (
                              <TableCell>
                                <Stack direction="column">
                                  <Box className="participant-key-box">
                                    <Typography className="participant-mobile-label">
                                      Location
                                    </Typography>
                                    <Typography className="participant-mobile-value">
                                      {row?.location}
                                    </Typography>
                                  </Box>
                                  <Box className="participant-key-box">
                                    <Typography className="participant-mobile-label">
                                      Team
                                    </Typography>
                                    <Typography className="participant-mobile-value">
                                      {row?.team}
                                    </Typography>
                                  </Box>
                                  <Box className="participant-key-box">
                                    <Typography className="participant-mobile-label">
                                      Role
                                    </Typography>
                                    <Typography className="participant-mobile-value">
                                      {row?.role}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                            ) : (
                              <>
                                <TableCell>{row?.location}</TableCell>
                                <TableCell>{row?.team}</TableCell>
                                <TableCell>{row?.role}</TableCell>
                              </>
                            )}
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={users.filter(handleSearchFilter).length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: isMobileDisplay ? "0.9rem" : "1rem",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: isMobileDisplay ? "0.9rem" : "1rem",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    fontSize: isMobileDisplay ? "0.9rem" : "1rem",
                  },
                }}
              />
            </Paper>
          </MDBox>
          <MDBox sx={{ display: "flex", justifyContent: "end", mt: 1 }}>
            <MDButton
              color="gray80"
              onClick={handleClose}
              sx={{ mt: 1, mr: 1, position: "relative" }}
            >
              Cancel
            </MDButton>
            <MDButton
              color="primaryRed"
              disabled={submitLoading}
              sx={{ mt: 1, position: "relative" }}
              onClick={handleParticipantsSelect}
            >
              {submitLoading ? "Loading..." : "Submit"}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default SelectParticipants;
